
import { defineComponent, reactive, onBeforeMount } from 'vue'

import NewButton from '@/ui-kit/NewButton/index.vue'
import ColumnsIcon from '@/assets/icons/columns.svg'
import NewColumnsController from '@/ui-kit/NewColumnsController/index.vue'
import NewTable from '@/ui-kit/NewTable/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import NewPagination from '@/common/new-components/NewPagination/index.vue'
import NewLimit from '@/common/new-components/NewLimit/index.vue'
import NewViewManager from '@/ui-kit/NewViewManager/index.vue'
import List from '@/common/components/UI/List/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'

import RppGeneralTabs from '@/modules/RPPModule/components/RppGeneralTabs/index.vue'
import RppLtlFilters from '@/modules/RPPModule/components/RppLtlFilters/index.vue'
import RppLtlListItem from '@/modules/RPPModule/components/RppLtlListItem/index.vue'
import ExportLtlExcelModal from '@/modules/RPPModule/components/ExportLtlExcelModal/index.vue'
import RppUpdateModal from '@/modules/RPPModule/components/RppUpdateModal/index.vue'
import RppMassChangeStatus from '@/modules/RPPModule/components/RppMassChangeStatus/index.vue'

import { LtlRpp, GetLtlRppParams } from '@/core/types/rpp.types'

import { rpp } from '@/core/realisations/rpp.realisation'

import useNotifications from '@/common/composable/useNotifications'
import { useFile } from '@/common/composable/useFile'

const TABLE_COLUMNS = [
    {
        key: 'pbsNumber',
        name: 'Номер РПП',
    },
    {
        key: 'pbsDate',
        name: 'Дата РПП',
    },
    {
        key: 'tripNumber',
        name: 'Номер рейса',
    },
    {
        key: 'direction',
        name: 'Направление',
    },
    {
        key: 'carrier',
        name: 'Перевозчик',
    },
    {
        key: 'statusName',
        name: 'Статус',
    },
    {
        key: 'ttns',
        name: 'ТТН',
    },
    {
        key: 'costPerWeight',
        name: 'тг/кг',
    },
    {
        key: 'costPerVolume',
        name: 'тг/м³',
    },
    {
        key: 'costOfTransportation',
        name: 'Стоимость перевозки',
    },
    {
        key: 'costOfTransportationWithoutNds',
        name: 'Стоимость без НДС',
    },
    {
        key: 'actNumber',
        name: 'Номер акта',
    },
    {
        key: 'actDate',
        name: 'Дата акта',
    },
    {
        key: 'totalWeight',
        name: 'Физический вес',
    },
    {
        key: 'totalCubature',
        name: 'Объемный вес',
    },
    {
        key: 'type_of_transportation',
        name: 'Вид перевозки',
    },
    {
        key: 'capacity',
        name: 'Вместимость',
    },
    {
        key: 'car',
        name: 'Автомобиль',
    },
    {
        key: 'logist',
        name: 'Логист',
    },
    {
        key: 'actions',
        name: '',
    },
]

export default defineComponent({
    components: {
        NewButton,
        ColumnsIcon,
        NewColumnsController,
        NewTable,
        RppGeneralTabs,
        RppLtlFilters,
        Loader,
        PageTitle,
        NewPagination,
        NewLimit,
        NewViewManager,
        List,
        Cheap,
        RppLtlListItem,
        ExportLtlExcelModal,
        RppUpdateModal,
        TheTextarea,
        NewModal,
        RppMassChangeStatus,
        NewCheckbox,
    },
    setup() {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_failed: false,
            is_export: false,
            cost_comparison: {
                costPerWeightStandard: null as number | null,
                costPerWeightActual: null as number | null,
                costPerCubatureStandard: null as number | null,
                costPerCubatureActual: null as number | null,
                costDifference: null as number | null,
            },
            list: [] as LtlRpp[],
            columns: TABLE_COLUMNS,
            is_columns_controller_opened: false,
            filters: {
                page: 1,
                limit: 50,
                act_number: null as string | null,
                status_id: null as number | null,
                company_id: null as number | null,
                user_id: null as number | null,
                act_date: null as string | null,
                from_city_id: null as number | null,
                where_city_id: null as number | null,
                turnover_date_from: null as string | null,
                turnover_date_to: null as string | null,
                trip_number: null as string | null,
                ttn_number: null as string | null,
                is_above_cost_standard: null as number | null,
            },
            pagination: {
                total_pages: 1,
                total_items: 1,
            },
            view: 'table' as 'table' | 'list',
            comment: null as string | null,
            is_submit: false,
            is_submit_loading: false,
            rpp_id: null as number | null,
            paymentBySupplierIds: [] as number[],
        })

        const mass_confirm_state = reactive({
            is_opened: false,
        })

        const update_state = reactive({
            is_opened: false,
            rpp_id: null as number | null,
        })

        const openColumnsController = () => {
            state.is_columns_controller_opened = true
        }

        const closeColumnsController = () => {
            state.is_columns_controller_opened = false
        }

        const loadRppList = async () => {
            unselectAllPaymentBySupplierIds()

            try {
                state.is_loading = true

                const response = await rpp.getLtlRppList({
                    page: state.filters.page,
                    limit: state.filters.limit,
                    act_number: state.filters.act_number,
                    status_id: state.filters.status_id,
                    company_id: state.filters.company_id,
                    user_id: state.filters.user_id,
                    act_date: state.filters.act_date,
                    from_city_id: state.filters.from_city_id,
                    where_city_id: state.filters.where_city_id,
                    turnover_date_from: state.filters.turnover_date_from,
                    turnover_date_to: state.filters.turnover_date_to,
                    trip_number: state.filters.trip_number,
                    ttn_number: state.filters.ttn_number,
                    is_above_cost_standard: state.filters.is_above_cost_standard,
                })

                state.list = response.data.map((item) => {
                    return {
                        ...item,
                        isRed: item.isAboveCostStandard,
                    }
                })
                state.pagination.total_pages = response.meta.last_page
                state.pagination.total_items = response.meta.total

                state.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка произошла ошибка',
                })
                state.is_failed = true
            } finally {
                state.is_loading = false
            }
        }

        const onFiltersSubmit = (filters: GetLtlRppParams) => {
            state.filters.act_number = filters.act_number as string | null
            state.filters.status_id = filters.status_id as number | null
            state.filters.company_id = filters.company_id as number | null
            state.filters.user_id = filters.user_id as number | null
            state.filters.act_date = filters.act_date as string | null
            state.filters.from_city_id = filters.from_city_id as number | null
            state.filters.where_city_id = filters.where_city_id as number | null
            state.filters.turnover_date_from = filters.turnover_date_from as string | null
            state.filters.turnover_date_to = filters.turnover_date_to as string | null
            state.filters.trip_number = filters.trip_number as string | null
            state.filters.ttn_number = filters.ttn_number as string | null
            state.filters.is_above_cost_standard = filters.is_above_cost_standard as number | null

            state.filters.page = 1

            loadRppList()
        }

        const onFiltersReset = () => {
            state.filters.act_number = null
            state.filters.status_id = null
            state.filters.company_id = null
            state.filters.user_id = null
            state.filters.act_date = null
            state.filters.from_city_id = null
            state.filters.where_city_id = null
            state.filters.turnover_date_from = null
            state.filters.turnover_date_to = null
            state.filters.trip_number = null
            state.filters.ttn_number = null
            state.filters.is_above_cost_standard = null

            state.filters.page = 1

            loadRppList()
        }

        const onRowClicked = (row: LtlRpp) => {
            console.log(row)
        }

        const onLimitChange = (limit: number) => {
            state.filters.limit = limit
            loadRppList()
        }

        const onChangePage = (page: number) => {
            state.filters.page = page
            loadRppList()
        }

        onBeforeMount(loadRppList)

        const onStartUpdateRpp = (rpp_id: number) => {
            update_state.rpp_id = rpp_id
            update_state.is_opened = true
        }

        const onStopUpdateRpp = () => {
            update_state.is_opened = false
            update_state.rpp_id = null
        }

        const loadCostComparison = async (tripId: number) => {
            try {
                state.is_submit_loading = true

                const response = await rpp.getLtlRppCostComparison(tripId)
                if (response && response.data) {
                    state.cost_comparison.costPerWeightStandard =
                        response.data.costPerWeightStandard
                    state.cost_comparison.costPerWeightActual = response.data.costPerWeightActual
                    state.cost_comparison.costPerCubatureStandard =
                        response.data.costPerCubatureStandard
                    state.cost_comparison.costPerCubatureActual =
                        response.data.costPerCubatureActual
                    state.cost_comparison.costDifference = response.data.costDifference
                }
            } catch (error) {
                console.error(error)
            } finally {
                state.is_submit_loading = false
            }
        }

        const resetCostComparison = () => {
            state.cost_comparison.costPerWeightStandard = null
            state.cost_comparison.costPerWeightActual = null
            state.cost_comparison.costPerCubatureStandard = null
            state.cost_comparison.costPerCubatureActual = null
            state.cost_comparison.costDifference = null
        }

        const toggleSubmit = (id?: number, tripId?: number) => {
            resetCostComparison()
            state.comment = null
            state.is_submit = !state.is_submit

            if (id && tripId) {
                state.rpp_id = id
                loadCostComparison(tripId)
            }
        }

        const submit = async () => {
            if (!state.rpp_id) return

            try {
                state.is_submit_loading = true

                const payload = {
                    statusCode: 'confirmed',
                    comment: state.comment || '',
                    paymentBySupplierIds: [state.rpp_id],
                }

                await rpp.submitRpp(payload)
                notifications.notify({
                    title: 'РПП успешно подтвержден!',
                    type: 'success',
                })
                toggleSubmit()
                loadRppList()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При подтверждении РПП произошла ошибка',
                })
            } finally {
                state.is_submit_loading = false
            }
        }

        const { saveFile } = useFile()

        const printRpp = async (id: number) => {
            try {
                state.is_loading = true

                const response = await rpp.exportLtlByIdExcel(id)
                saveFile(response, `LTL РПП (ID_${id}) распечатка.xlsx`)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Произошла ошибка при скачивании`,
                })
            } finally {
                state.is_loading = false
            }
        }

        const onMassConfirm = () => {
            mass_confirm_state.is_opened = true
        }

        const onMassConfirmClose = () => {
            mass_confirm_state.is_opened = false
        }

        const onMassConfirmSuccess = () => {
            onMassConfirmClose()
            unselectAllPaymentBySupplierIds()
            loadRppList()
        }

        const selectAllPaymentBySupplierIds = () => {
            state.paymentBySupplierIds = state.list
                .filter((paymentBySupplier) => (paymentBySupplier as any).statusId === 11)
                .map((paymentBySupplier) => paymentBySupplier.id)
        }

        const unselectAllPaymentBySupplierIds = () => {
            state.paymentBySupplierIds = []
        }

        const selectPaymentBySupplierId = (paymentBySupplier: LtlRpp) => {
            if (state.paymentBySupplierIds.includes(paymentBySupplier.id)) {
                unselectPaymentBySupplierId(paymentBySupplier)
                return
            }

            state.paymentBySupplierIds.push(paymentBySupplier.id)
        }

        const unselectPaymentBySupplierId = (paymentBySupplier: LtlRpp) => {
            const index = state.paymentBySupplierIds.findIndex((id) => id === paymentBySupplier.id)

            if (index >= 0) {
                state.paymentBySupplierIds.splice(index, 1)
            }
        }

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '-'

            const dateFirst = dateStr.split(' ')[0]
            return dateFirst.split('-').reverse().join('.')
        }

        return {
            TABLE_COLUMNS,
            state,
            mass_confirm_state,
            openColumnsController,
            closeColumnsController,
            onFiltersSubmit,
            onFiltersReset,
            onRowClicked,
            onLimitChange,
            onChangePage,
            update_state,
            onStartUpdateRpp,
            onStopUpdateRpp,
            toggleSubmit,
            submit,
            printRpp,
            selectAllPaymentBySupplierIds,
            selectPaymentBySupplierId,
            unselectAllPaymentBySupplierIds,
            unselectPaymentBySupplierId,
            onMassConfirm,
            onMassConfirmClose,
            onMassConfirmSuccess,
            formatDate,
        }
    },
})
